import {
  asapScheduler,
  asyncScheduler,
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  from,
  map,
  mergeMap,
  Observable,
  observeOn,
  of,
  startWith,
  Subscription,
  switchMap,
  zip,
} from 'rxjs';
import { ObjectUtils } from './object.utils';

import Field from '@arcgis/core/layers/support/Field';
import WMSLayer from '@arcgis/core/layers/WMSLayer';
import WMSSublayer from '@arcgis/core/layers/support/WMSSublayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import Sublayer from '@arcgis/core/layers/support/Sublayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import EsriMap from '@arcgis/core/Map';
import Polyline from '@arcgis/core/geometry/Polyline';
import Polygon from '@arcgis/core/geometry/Polygon';

import MapView from '@arcgis/core/views/MapView';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import * as geodesicUtils from '@arcgis/core/geometry/support/geodesicUtils';
import * as geometryJsonUtils from '@arcgis/core/geometry/support/jsonUtils';
import * as symbolUtils from '@arcgis/core/symbols/support/symbolUtils';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import Extent from '@arcgis/core/geometry/Extent';

import * as Identify from '@arcgis/core/rest/identify';

import * as print from '@arcgis/core/rest/Print';
import PrintParameters from '@arcgis/core/rest/support/PrintParameters';

import * as projection from '@arcgis/core/geometry/projection';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import CSVLayer from '@arcgis/core/layers/CSVLayer';
import WFSLayer from '@arcgis/core/layers/WFSLayer';
import { MathUtils } from './math.utils';
import FieldInfoFormat from '@arcgis/core/popup/support/FieldInfoFormat';
import FieldInfo from '@arcgis/core/popup/FieldInfo';
import { FormatterUtils } from './formatter.utils';
import * as intl from '@arcgis/core/intl';
import Layer from '@arcgis/core/layers/Layer';
projection.load();

type Formatter = (value: any) => any;

interface WatchResponse<T = any> {
  newValue: T;
  oldValue: T;
  propertyName: string;
}

export type SublayerTypes = __esri.Sublayer | __esri.WMSSublayer;
const sublayerClassList = [Sublayer, WMSSublayer];

export type LayerWithChildren =
  | __esri.GroupLayer
  | __esri.MapImageLayer
  | __esri.WMSLayer
  | SublayerTypes;
const layerWithCollectionClassList = [
  GroupLayer,
  MapImageLayer,
  Sublayer,
  WMSLayer,
  WMSSublayer,
];

export type QueryableCountLayer =
  | __esri.FeatureLayer
  | __esri.CSVLayer
  | __esri.WFSLayer;
export type QueryableLayer = QueryableCountLayer | __esri.Sublayer;
const queryableLayerClassList = [FeatureLayer, CSVLayer, WFSLayer, Sublayer];

export type LayerWithScaleRule = __esri.ScaleRangeLayer | SublayerTypes;

export type AreaUnit =
  | 'square-millimeters'
  | 'square-centimeters'
  | 'square-decimeters'
  | 'square-meters'
  | 'square-kilometers'
  | 'square-inches'
  | 'square-feet'
  | 'square-yards'
  | 'square-miles'
  | 'square-us-feet'
  | 'acres'
  | 'ares'
  | 'hectares';

export type LengthUnit =
  | 'millimeters'
  | 'centimeters'
  | 'decimeters'
  | 'meters'
  | 'kilometers'
  | 'inches'
  | 'feet'
  | 'yards'
  | 'miles'
  | 'nautical-miles'
  | 'us-feet';

const symbolRenderedAsHTMLCache = new Map<string, Promise<HTMLElement>>();
const symbolRenderedAsUrlCache = new Map<string, Promise<string>>();

const defaultScheduler = asyncScheduler;

export class EsriUtils {
  static normalizeGeomType(type: string) {
    return type?.replace('esriGeometry', '')?.toLowerCase();
  }

  static rightClicks(target: __esri.MapView) {
    return EsriUtils.clicks(target).pipe(filter((e) => e.button === 2));
  }

  static leftClicks(target: __esri.MapView) {
    return EsriUtils.clicks(target).pipe(filter((e) => e.button === 0));
  }

  static clicks(target: __esri.MapView) {
    return EsriUtils.on(target, 'click');
  }

  static leftImmediateClicks(target: __esri.MapView) {
    return EsriUtils.clicks(target).pipe(filter((e) => e.button === 0));
  }

  static immediateClicks(target: __esri.MapView) {
    return EsriUtils.on(target, 'immediate-click');
  }

  static on<T = any>(
    target: __esri.Evented,
    eventOrEvents: string | string[]
  ): Observable<T> {
    return new Observable<T>((subscriber) => {
      const handle = target.on(eventOrEvents, (e) => {
        subscriber.next(e);
      });

      subscriber.add(() => {
        handle?.remove();
      });
    });
  }

  static watch<T = any>(
    accessor: __esri.Accessor,
    path: string | string[],
    sync?: boolean
  ) {
    return new Observable<WatchResponse<T>>((subscriber) => {
      const handle = accessor.watch(
        path,
        (newValue, oldValue, propertyName) => {
          subscriber.next({ newValue, oldValue, propertyName });
        },
        sync
      );

      subscriber.add(() => {
        handle?.remove();
      });
    });
  }

  static whenOnly(
    target:
      | __esri.MapView
      | __esri.Loadable
      | Array<__esri.MapView | __esri.Loadable>
  ) {
    let promise: Promise<any>;
    if (Array.isArray(target)) {
      promise = Promise.all(target.map((x) => x.when()));
    } else {
      promise = target.when();
    }

    return from(promise);
  }

  static isLoaded(target: __esri.MapView | __esri.Loadable | SublayerTypes) {
    const loadableTarget = target as __esri.Loadable;
    return (
      (target as __esri.MapView).ready ||
      loadableTarget.loadStatus === 'loaded' ||
      !('loadStatus' in loadableTarget || 'ready' in (target as __esri.MapView))
    );
  }

  static when(
    target:
      | __esri.MapView
      | __esri.Loadable
      | SublayerTypes
      | Array<__esri.MapView | __esri.Loadable | SublayerTypes>
  ): Observable<any> {
    const loadableTarget = target as __esri.Loadable;
    if (Array.isArray(target)) {
      return zip(target.map((x) => EsriUtils.when(x)));
    } else if (EsriUtils.isLoaded(target)) {
      return from(Promise.resolve(target));
    } else {
      return EsriUtils.whenOnly(loadableTarget);
    }
  }

  static load(
    target:
      | __esri.Loadable
      | SublayerTypes
      | Array<__esri.Loadable | SublayerTypes>
  ): Observable<any> {
    const loadableTarget = target as __esri.Loadable;
    if (Array.isArray(target)) {
      return zip(target.map((x) => EsriUtils.load(x)));
    } else if (EsriUtils.isLoaded(target)) {
      return from(Promise.resolve(target));
    } else {
      return new Observable<any>((subscriber) => {
        if (loadableTarget.loadStatus === 'not-loaded') {
          const abortController = new AbortController();
          subscriber.add(() => {
            loadableTarget.cancelLoad();

            abortController.abort();
          });

          loadableTarget.load(abortController.signal);
        }

        subscriber.add(EsriUtils.when(loadableTarget).subscribe(subscriber));
      });
    }
  }

  static loadAllLayers(
    layerOrLayers:
      | __esri.Layer
      | SublayerTypes
      | Array<__esri.Layer | SublayerTypes>
  ): Observable<any> {
    if (Array.isArray(layerOrLayers)) {
      return zip(layerOrLayers.map((x) => EsriUtils.loadAllLayers(x)));
    } else {
      return EsriUtils.load(layerOrLayers).pipe(
        mergeMap((e) => {
          let collection = EsriUtils.getSublayersCollection(
            layerOrLayers as any
          );

          if (collection?.length) {
            return EsriUtils.loadAllLayers(collection.toArray());
          } else {
            return of(e);
          }
        })
      );
    }
  }

  static loadLayerFromPortalItem<T = Layer>(portalItem: __esri.PortalItem) {
    return new Observable<T>((subscriber) => {
      Layer.fromPortalItem({
        portalItem,
      })
        .then((layer) => {
          if (subscriber.closed) {
            layer?.destroy();
            return;
          }

          subscriber.add(
            EsriUtils.loadAllLayers(layer).subscribe(() => {
              if (subscriber.closed) {
                layer?.destroy();
                return;
              }

              subscriber.next(layer as T);
              subscriber.complete();
            })
          );
        })
        .catch((e) => {
          subscriber.error(e);
        });
    });
  }

  static destroyLayersRecursive(
    layerOrLayers:
      | Array<__esri.Layer | SublayerTypes>
      | __esri.Layer
      | SublayerTypes
  ) {
    EsriUtils.findRecursiveLayer(layerOrLayers, (x) => {
      EsriUtils.destroyLayersRecursive(x);

      return false;
    });

    if (!Array.isArray(layerOrLayers)) {
      if ((layerOrLayers as any)?.portalItem) {
        (layerOrLayers as any).portalItem = null;
      }

      (layerOrLayers as __esri.Loadable)?.cancelLoad?.();

      const collection = EsriUtils.getSublayersCollection(layerOrLayers as any);
      collection?.removeAll();

      layerOrLayers?.destroy();

      if ('parent' in layerOrLayers) {
        const parentCollection = EsriUtils.getSublayersCollection(
          layerOrLayers.parent as any
        );
        parentCollection?.remove(layerOrLayers);
      }
    }
  }

  static isQueryableLayer(layer: any) {
    return queryableLayerClassList.some((x) => layer instanceof x);
  }

  static isLayerWithScaleRule(layer: any) {
    return !!('maxScale' in layer && 'minScale' in layer);
  }

  static queryFeatureCount(
    layer: QueryableCountLayer,
    query: __esri.Query
  ): Observable<number> {
    return createRequestToObservable((signal) =>
      layer.queryFeatureCount(query, { signal })
    );
  }

  static queryFeatures(
    layer: QueryableLayer,
    query: __esri.Query
  ): Observable<__esri.FeatureSet> {
    return createRequestToObservable((signal) =>
      layer.queryFeatures(query, { signal })
    );
  }

  static queryFeaturesAll(
    layer: QueryableCountLayer,
    query: __esri.Query,
    limitRequestsCount = 10
  ) {
    query = query.clone();

    return new Observable<{
      totalCount: number;
      featureSet: __esri.FeatureSet;
    }>((subscriber) => {
      if (typeof query.start !== 'number') {
        query.start = 0;
      }

      const limit = query.num ?? Number.MAX_SAFE_INTEGER;
      if (typeof query.num !== 'number') {
        query.num = Math.min(limit, 2000);
      }

      subscriber.add(
        EsriUtils.queryFeatureCount(layer, query).subscribe({
          next: (totalCount) => {
            const chunkCount = Math.ceil(
              Math.min(totalCount, limit) / query.num
            );

            let featureSetFirst: __esri.FeatureSet;

            let observables = new Array<Observable<__esri.Graphic[]>>(
              chunkCount
            );

            const recursiveFn = (
              start: number = 0,
              parentSubject?: BehaviorSubject<number>
            ) => {
              let finishLastRequestsSubject = new BehaviorSubject<number>(0);

              let end = start + Math.min(limitRequestsCount, chunkCount + 1);

              for (let i = start; i < end; ++i) {
                const queryCloned = query.clone();
                queryCloned.start += query.num * i;

                let observable = EsriUtils.queryFeatures(
                  layer,
                  queryCloned
                ).pipe(
                  map((featureSet) => {
                    if (i === 0) {
                      featureSetFirst = featureSet;
                    }

                    const value = finishLastRequestsSubject.getValue() + 1;
                    finishLastRequestsSubject.next(value);

                    return featureSet.features;
                  })
                );

                if (parentSubject) {
                  const observableAux = observable;
                  observable = parentSubject.pipe(
                    first((value) => value >= limitRequestsCount),
                    mergeMap(() => observableAux)
                  );
                }

                observables[i] = observable;
              }

              start += limitRequestsCount;
              end = Math.min(chunkCount, end + limitRequestsCount);
              if (end - start > 0) {
                recursiveFn(start, finishLastRequestsSubject);
              }
            };

            recursiveFn();

            subscriber.add(
              zip(observables).subscribe({
                next: (featureListGrouped) => {
                  featureSetFirst.features = featureListGrouped.flat();

                  if (featureSetFirst.features.length > limit) {
                    featureSetFirst.features = featureSetFirst.features.slice(
                      0,
                      limit
                    );
                  }

                  subscriber.next({ totalCount, featureSet: featureSetFirst });
                  subscriber.complete();
                },
                error: (err) => {
                  subscriber.error(err);
                },
              })
            );
          },
          error: (err) => {
            subscriber.error(err);
          },
        })
      );
    }).pipe(observeOn(defaultScheduler));
  }

  static queryAttachments(
    layer: __esri.FeatureLayer,
    query: __esri.AttachmentQuery | __esri.AttachmentQueryProperties
  ) {
    return createRequestToObservable((signal) =>
      layer.queryAttachments(query, { signal })
    ).pipe(
      map((res) => {
        const map = new Map<number, __esri.AttachmentInfo[]>();
        for (const [key, list] of Object.entries(res)) {
          map.set(Number(key), list as any);
        }

        return map;
      })
    );
  }

  static queryRelatedFeatures(
    layer: __esri.FeatureLayer,
    query: __esri.RelationshipQuery
  ) {
    return createRequestToObservable((signal) =>
      layer.queryRelatedFeatures(query, { signal })
    );
  }

  static applyEdits(
    layer: __esri.FeatureLayer,
    edits: __esri.FeatureLayerApplyEditsEdits
  ): Observable<__esri.EditsResult> {
    return new Observable<__esri.EditsResult>((subscriber) => {
      layer
        .applyEdits(edits, { rollbackOnFailureEnabled: true })
        .then((res) => {
          subscriber.next(res);
          subscriber.complete();
        })
        .catch((err) => {
          subscriber.error(err);
        });
    });
  }

  static fetchFeaturesByPopup(
    popup: __esri.Popup,
    screenPoint: __esri.ScreenPoint,
    clickEvent?: __esri.ViewClickEvent
  ) {
    return createRequestToObservable((signal) =>
      popup.fetchFeatures(screenPoint, {
        event: clickEvent,
        signal,
      })
    );
  }

  static fetchDataByPortalItem(
    portalItem: __esri.PortalItem,
    responseType?:
      | 'json'
      | 'xml'
      | 'text'
      | 'blob'
      | 'array-buffer'
      | 'document'
  ) {
    return createRequestToObservable((signal) =>
      portalItem.fetchData(responseType ?? 'json', {
        signal,
      })
    );
  }

  static highlight(
    layerView: __esri.FeatureLayerView,
    target?: __esri.Graphic | __esri.Graphic[] | number | number[]
  ) {
    return new Observable((subscriber) => {
      const handle = layerView.highlight(target);

      subscriber.add(() => {
        handle?.remove();
      });
    });
  }

  static goTo(
    mapView: __esri.MapView,
    target: __esri.GoToTarget2D,
    options?: __esri.GoToOptions2D
  ) {
    return createRequestToObservable((signal) => {
      options = Object.assign(options ?? {}, {
        signal,
      });

      return mapView.goTo(target, options);
    }, options?.signal);
  }

  static isGeomEquals(geomA: __esri.Geometry, geomB: __esri.Geometry): boolean {
    if (geomA === geomB) {
      return true;
    }

    return geometryEngine.equals(geomA, geomB);
  }

  static isViewpointEquals(
    viewpointA: __esri.Viewpoint,
    viewpointB: __esri.Viewpoint
  ): boolean {
    if (viewpointA === viewpointB) {
      return true;
    }

    if (!viewpointA || !viewpointB) {
      return false;
    }

    if (
      !ObjectUtils.equalsByFields(viewpointA, viewpointB, ['scale', 'rotation'])
    ) {
      return false;
    }

    if (
      !ObjectUtils.equalsByFields(viewpointA.camera, viewpointB.camera, [
        'fov',
        'heading',
        'tilt',
      ])
    ) {
      return false;
    }

    if (
      !ObjectUtils.equals(
        viewpointA.camera?.position,
        viewpointB.camera?.position
      )
    ) {
      return false;
    }

    if (
      viewpointA.targetGeometry === viewpointB.targetGeometry &&
      (!viewpointA.targetGeometry || !viewpointB.targetGeometry)
    ) {
      return false;
    }

    return EsriUtils.isGeomEquals(
      viewpointA.targetGeometry,
      viewpointB.targetGeometry
    );
  }

  static project<T extends __esri.Geometry>(
    geometry: T,
    outSpatialReference: Partial<SpatialReference>
  ): T {
    if (
      !outSpatialReference ||
      geometry.spatialReference?.wkid === outSpatialReference.wkid
    ) {
      return geometry;
    }

    geometry = projection.project(geometry, outSpatialReference) as any;
    return geometry;
  }

  static identify(
    url: string,
    parameters: Partial<__esri.IdentifyParameters>
  ): Observable<{ results: Array<__esri.IdentifyResult> }> {
    return createRequestToObservable((signal) =>
      Identify.identify(url, parameters as any, {
        signal,
      })
    );
  }

  static createExtentFromPoint(point: Partial<__esri.Point>): Extent {
    let extent = point.extent?.clone();
    if (!extent) {
      extent = new Extent({
        xmin: point.x,
        ymin: point.y,
        xmax: point.x,
        ymax: point.y,
        spatialReference: point.spatialReference,
      });
    }

    return extent;
  }

  static geometryFromJson(jsonObj: any): __esri.Geometry {
    if (!jsonObj) {
      return null;
    }

    if (typeof jsonObj === 'string') {
      jsonObj = JSON.parse(jsonObj);
    }

    if (jsonObj?.initialized) {
      return jsonObj;
    }

    const geom = geometryJsonUtils.fromJSON(jsonObj);
    return geom;
  }

  static resolveLayerUrl(url: string, layerId?: number) {
    if (typeof layerId === 'number') {
      const layerIdStr = layerId.toString();
      url = url.replace(/\d{1,}$/i, layerIdStr);
      if (!url.endsWith(layerIdStr)) {
        if (!url.endsWith('/')) {
          url += '/';
        }

        url += layerIdStr;
      }
    }

    return url;
  }

  static convertPxToDistanceXCoords(
    mapView: __esri.MapView,
    px: number
  ): number {
    const startP = mapView.toMap({ y: 0, x: 0 });
    const endP = mapView.toMap({ y: 0, x: px });
    const diff = endP.x - startP.x;
    return diff;
  }

  static convertPxToDistanceYCoords(
    mapView: __esri.MapView,
    px: number
  ): number {
    const startP = mapView.toMap({ y: 0, x: 0 });
    const endP = mapView.toMap({ y: px, x: 0 });
    const diff = endP.y - startP.y;
    return diff;
  }

  static geodesicBuffer(
    geometry: __esri.Geometry,
    distanceInMeters: number
  ): __esri.Polygon {
    return geometryEngine.geodesicBuffer(
      geometry,
      distanceInMeters,
      'meters'
    ) as any;
  }

  static getCentroidFromLine(line: __esri.Polyline): __esri.Point {
    const polygon = new Polygon({
      rings: line.paths,
      spatialReference: line.spatialReference,
    });

    return polygon.centroid;
  }

  static rotate<T extends __esri.Geometry>(
    geometry: T,
    angle: number,
    rotationOrigin?: __esri.Point
  ): T {
    return geometryEngine.rotate(geometry, angle, rotationOrigin) as T;
  }

  static getRadiusLine(
    circle: __esri.Polygon,
    type?: 'vertical' | 'horizontal'
  ): __esri.Polyline {
    const firstPoint = circle.centroid;
    const secondPoint = circle.getPoint(0, 0);

    let line = new Polyline({
      paths: [
        [
          [firstPoint.x, firstPoint.y],
          [secondPoint.x, secondPoint.y],
        ],
      ],
      spatialReference: circle.spatialReference,
    });

    switch (type) {
      case 'horizontal':
        if (firstPoint.x === secondPoint.x) {
          line = EsriUtils.rotate(line, 90, firstPoint);
        }
        break;
      case 'vertical':
        if (firstPoint.y === secondPoint.y) {
          line = EsriUtils.rotate(line, 90, firstPoint);
        }
        break;
    }

    return line;
  }

  static calculateRadius(
    geom: __esri.Polygon,
    unit: AreaUnit = 'square-meters'
  ) {
    const area = EsriUtils.calculateArea(geom, unit);
    return EsriUtils.calculateRadiusByArea(area);
  }

  static calculateRadiusByArea(area: number) {
    return Math.sqrt(area / Math.PI);
  }

  static calculateArea(geom: __esri.Polygon, unit?: AreaUnit) {
    geom = EsriUtils.project(geom, { wkid: 4326 });
    let [area] = geodesicUtils.geodesicAreas([geom], unit);
    area = MathUtils.positivize(area);
    return area;
  }

  static calculateLength(geom: __esri.Polyline, unit?: LengthUnit) {
    geom = EsriUtils.project(geom, { wkid: 4326 });
    const [area] = geodesicUtils.geodesicLengths([geom], unit);
    return area;
  }

  static takeScreenshotAsImageData(
    view: MapView,
    options?: __esri.MapViewTakeScreenshotOptions
  ) {
    return new Observable<ImageData>((subscriber) => {
      view
        .takeScreenshot(options)
        .then(({ data }) => {
          if (subscriber.closed) {
            return;
          }

          subscriber.next(data);
          subscriber.complete();
        })
        .catch((err) => {
          subscriber.error(err);
        });
    });
  }

  static takeScreenshotAsCanvas(
    view: MapView,
    options?: __esri.MapViewTakeScreenshotOptions
  ) {
    return EsriUtils.takeScreenshotAsImageData(view, options).pipe(
      map((data) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = data.width;
        canvas.height = data.height;
        ctx.putImageData(data, 0, 0);

        return canvas;
      })
    );
  }

  static takeScreenshotAsBlob(
    view: MapView,
    options?: __esri.MapViewTakeScreenshotOptions
  ) {
    const format = options?.format ?? 'png';
    const quality = options?.quality ?? 100;

    options = Object.assign(
      {
        format,
        quality,
      },
      options ?? {}
    );

    return EsriUtils.takeScreenshotAsCanvas(view, options).pipe(
      mergeMap((canvas) => {
        return new Observable<Blob>((subscriber) => {
          canvas.toBlob(
            (blob) => {
              if (subscriber.closed) {
                return;
              }

              if (!blob) {
                subscriber.error(new Error('Could not generate blob'));
                return;
              }

              subscriber.next(blob);
              subscriber.complete();
            },
            'image/' + format,
            quality
          );
        });
      })
    );
  }

  static fieldFromJson(jsonObj: any) {
    if (!jsonObj) {
      return null;
    }

    if (typeof jsonObj === 'string') {
      jsonObj = JSON.parse(jsonObj);
    }

    const newField = Field.fromJSON(jsonObj);
    return newField;
  }

  static cloneField(field: Field) {
    field = Field.fromJSON(field.toJSON());
    return field;
  }

  private static _generateSymbolKey(symbol: __esri.Symbol) {
    return JSON.stringify(symbol);
  }

  private static _renderSymbolPreviewHTML(
    symbolKey: string,
    symbol: __esri.Symbol
  ) {
    let iconPromise: Promise<HTMLElement>;
    if (symbolRenderedAsHTMLCache.has(symbolKey)) {
      iconPromise = symbolRenderedAsHTMLCache.get(symbolKey);
    } else {
      iconPromise = Promise.resolve().then(() =>
        symbolUtils.renderPreviewHTML(symbol)
      );
      symbolRenderedAsHTMLCache.set(symbolKey, iconPromise);
    }

    return iconPromise.then((el) => el.cloneNode(true) as HTMLElement);
  }

  static renderSymbolPreviewHTML(symbol: __esri.Symbol) {
    const symbolKey = EsriUtils._generateSymbolKey(symbol);
    return EsriUtils._renderSymbolPreviewHTML(symbolKey, symbol);
  }

  static async renderSymbolPreviewImageUrl(
    symbol: __esri.Symbol
  ): Promise<string> {
    let iconPromise: Promise<string>;

    const symbolKey = EsriUtils._generateSymbolKey(symbol);
    if (symbolRenderedAsUrlCache.has(symbolKey)) {
      iconPromise = symbolRenderedAsUrlCache.get(symbolKey);
    } else {
      iconPromise = EsriUtils._renderSymbolPreviewHTML(symbolKey, symbol).then(
        (el) => {
          const svg = el.firstElementChild as SVGElement;

          if (!svg.hasAttribute('viewBox')) {
            const width = svg.getAttribute('width');
            const height = svg.getAttribute('height');
            svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
          }

          const svgStr = svg.outerHTML;
          const blob = new Blob([svgStr], { type: 'image/svg+xml' });
          return URL.createObjectURL(blob);
        }
      );
      symbolRenderedAsUrlCache.set(symbolKey, iconPromise);
    }

    return iconPromise;
  }

  static isLayerInScale(layer: any, scale: number) {
    if (EsriUtils.isLayerWithScaleRule(layer)) {
      return (
        (layer.minScale === 0 || scale < layer.minScale) &&
        (layer.maxScale === 0 || scale > layer.maxScale)
      );
    }

    return false;
  }

  static isRecursiveLayerInScale(
    layer: __esri.Layer | SublayerTypes,
    scale: number
  ) {
    const checkFn = (layer: __esri.Layer | SublayerTypes) => {
      return !EsriUtils.isLayerInScale(layer, scale);
    };

    if (checkFn(layer)) {
      return false;
    }

    return !EsriUtils.findParentRecursiveLayer(layer, checkFn);
  }

  static isRecursiveLayerVisibleAndInScale(
    layer: __esri.Layer | SublayerTypes,
    scale: number
  ) {
    const checkFn = (layer: __esri.Layer | SublayerTypes) => {
      return !(layer.visible && EsriUtils.isLayerInScale(layer, scale));
    };

    if (checkFn(layer)) {
      return false;
    }

    return !EsriUtils.findParentRecursiveLayer(layer, checkFn);
  }

  static isRecursiveLayerVisible(layer: __esri.Layer | SublayerTypes) {
    const checkFn = (layer: __esri.Layer | SublayerTypes) => {
      return !layer.visible;
    };

    if (checkFn(layer)) {
      return false;
    }

    return !EsriUtils.findParentRecursiveLayer(layer, checkFn);
  }

  static findParentRecursiveLayer(
    layer: __esri.Layer | SublayerTypes,
    predicate: (
      layer: __esri.Layer | SublayerTypes,
      level: number
    ) => boolean | void,
    deepLevel?: number
  ) {
    return _findParentRecursiveLayer(layer, predicate, deepLevel);
  }

  static isLayerWithCollection(layer: __esri.Layer | SublayerTypes) {
    return layerWithCollectionClassList.some((x) => layer instanceof x);
  }

  static isSubLayer(layer: any) {
    return sublayerClassList.some((x) => layer instanceof x);
  }

  static getSublayersCollection(layerOrMap: __esri.Map | LayerWithChildren) {
    let collection: __esri.Collection<__esri.Layer | SublayerTypes>;

    if (
      layerOrMap instanceof MapImageLayer ||
      layerOrMap instanceof Sublayer ||
      layerOrMap instanceof WMSLayer ||
      layerOrMap instanceof WMSSublayer
    ) {
      collection = layerOrMap.sublayers;
    } else if (
      layerOrMap instanceof GroupLayer ||
      layerOrMap instanceof EsriMap
    ) {
      collection = layerOrMap.layers;
    }

    return collection;
  }

  static removeRecursiveLayerRangeFrom(
    layerOrMap: __esri.Map | LayerWithChildren,
    predicate: (
      layer: __esri.Layer | SublayerTypes,
      index: number,
      parent: __esri.Map | LayerWithChildren,
      level: number
    ) => boolean | void
  ) {
    return _removeLayersFromRecursively(0, false, layerOrMap, predicate);
  }

  static removeRecursiveLayerFrom(
    layerOrMap: __esri.Map | LayerWithChildren,
    predicate: (
      layer: __esri.Layer | SublayerTypes,
      index: number,
      parent: __esri.Map | LayerWithChildren,
      level: number
    ) => boolean | void
  ) {
    return _removeLayersFromRecursively(0, true, layerOrMap, predicate);
  }

  static findRecursiveLayer(
    mapOrLayers:
      | __esri.Map
      | Array<__esri.Layer | SublayerTypes>
      | __esri.Layer
      | SublayerTypes,
    predicate: (
      layer: __esri.Layer | SublayerTypes,
      index: number,
      parent: __esri.Map | LayerWithChildren,
      level: number
    ) => boolean | void,
    deepLevel?: number
  ) {
    let parent: any;
    let layers: Array<__esri.Layer | SublayerTypes>;
    if (mapOrLayers instanceof EsriMap) {
      parent = mapOrLayers;
      layers = mapOrLayers.layers?.toArray();
    } else if (Array.isArray(mapOrLayers)) {
      parent = null;
      layers = mapOrLayers;
    } else {
      parent = mapOrLayers;
      layers =
        EsriUtils.getSublayersCollection(mapOrLayers as any)?.toArray() ?? [];
    }

    return _findRecursiveLayer(parent, layers, predicate, deepLevel);
  }

  static flatLayers(
    layers: Array<__esri.Layer | SublayerTypes>,
    deepLevel?: number
  ) {
    return _flatLayers(layers, deepLevel);
  }

  static getDeeperLayers(
    layerOrLayers:
      | __esri.Layer
      | SublayerTypes
      | Array<__esri.Layer | SublayerTypes>,
    deepLevel?: number
  ) {
    if (!Array.isArray(layerOrLayers)) {
      layerOrLayers = [layerOrLayers];
    }

    return _getDeeperLayers(layerOrLayers, deepLevel);
  }

  static observeDeepCollectionLayers(
    collection: __esri.Collection<__esri.Layer | SublayerTypes>,
    deepLevel?: number
  ) {
    return _observeDeepCollectionLayers(collection, deepLevel);
  }

  static isRecursiveLayerVisibleObservable(
    layer: __esri.Layer | SublayerTypes
  ) {
    return _isRecursiveLayerVisibleObservable(layer).pipe(
      distinctUntilChanged()
    );
  }

  static isLayerVisibleObservable(layer: __esri.Layer | SublayerTypes) {
    return _observeKeyParamLayer<boolean, __esri.Layer | SublayerTypes>(
      layer,
      'visible'
    ).pipe(distinctUntilChanged());
  }

  static observeLayerMaxScale(layer: LayerWithScaleRule) {
    return _observeKeyParamLayer<number, LayerWithScaleRule>(
      layer,
      'maxScale'
    ).pipe(distinctUntilChanged());
  }

  static observeLayerMinScale(layer: LayerWithScaleRule) {
    return _observeKeyParamLayer<number, LayerWithScaleRule>(
      layer,
      'minScale'
    ).pipe(distinctUntilChanged());
  }

  static intersects(
    geometry1: __esri.Geometry,
    geometry2: __esri.Geometry
  ): boolean {
    const aBool = geometryEngine.intersects(geometry1, geometry2);

    return aBool;
  }

  static printExecute(
    url: string,
    params: PrintParameters
  ): Observable<__esri.PrintResponse> {
    return createRequestToObservable((signal) =>
      print.execute(url, params, { signal })
    );
  }

  static handleFieldDomains(
    features: __esri.Graphic[],
    fields: __esri.Field[]
  ) {
    EsriUtils.mapFieldsFeaturesTo(
      features,
      fields,
      (feature, field, valueTransformed) => {
        feature.attributes[field.name] = valueTransformed;
      }
    );
  }

  static mapFeaturesTo(
    features: __esri.Graphic[],
    fields: __esri.Field[],
    consumer: (
      feature: __esri.Graphic,
      params: { field: __esri.Field; valueTransformed: any; value: any }[],
      index: number
    ) => void
  ) {
    const fieldCachesCache = new Map<string, Map<any, string>>();

    for (let i = 0; i < features.length; ++i) {
      const feature = features[i];

      const params = fields.map((field) => {
        let codedValuesCache = fieldCachesCache.get(field.name);
        if (!codedValuesCache) {
          codedValuesCache = new Map();
          fieldCachesCache.set(field.name, codedValuesCache);
        }

        const value = feature.attributes[field.name];
        const valueTransformed = this.getFieldCodedDomainValue(
          value,
          (field.domain as __esri.CodedValueDomain)?.codedValues,
          codedValuesCache
        );

        return { field, valueTransformed, value };
      });

      consumer(feature, params, i);
    }
  }

  static mapFieldsFeaturesTo(
    features: __esri.Graphic[],
    fields: __esri.Field[],
    consumer: (
      feature: __esri.Graphic,
      field: __esri.Field,
      valueTransformed: any,
      value: any
    ) => void
  ) {
    const fieldCachesCache = new Map<string, Map<any, string>>();

    for (const feature of features) {
      for (const field of fields) {
        let codedValuesCache = fieldCachesCache.get(field.name);
        if (!codedValuesCache) {
          codedValuesCache = new Map();
          fieldCachesCache.set(field.name, codedValuesCache);
        }

        const value = feature.attributes[field.name];
        const valueTransformed = this.getFieldCodedDomainValue(
          value,
          (field.domain as __esri.CodedValueDomain)?.codedValues,
          codedValuesCache
        );
        consumer(feature, field, valueTransformed, value);
      }
    }
  }

  static getFieldCodedDomainValue(
    code: any,
    codedValues: __esri.CodedValue[],
    cache?: Map<any, string>
  ) {
    let name: string;
    if (cache?.has(code)) {
      name = cache.get(code);
    } else {
      name = codedValues?.find((x) => x.code === code)?.name ?? code;
      cache?.set(code, name);
    }

    return name;
  }

  static setLocale(locale: string) {
    intl.setLocale(locale);
  }

  static format(
    value: any,
    field: __esri.Field | __esri.FieldInfo | __esri.FieldInfoFormat | string,
    layer?: QueryableLayer | SublayerTypes
  ) {
    return EsriUtils.buildFormatter(field, layer)?.(value) ?? value;
  }

  static buildFormatter(
    field:
      | __esri.Field
      | __esri.Domain
      | __esri.FieldInfo
      | __esri.FieldInfoFormat
      | string,
    layerOrLayers?:
      | QueryableLayer
      | SublayerTypes
      | Array<QueryableLayer | SublayerTypes>
  ) {
    let fieldFormatter: Formatter;

    if (field) {
      if (field instanceof FieldInfoFormat) {
        fieldFormatter = resolveFormatterByFieldInfoFormat(field);
      } else if (field instanceof FieldInfo) {
        fieldFormatter = resolveFormatterByFieldInfoFormat(field.format);

        if (!fieldFormatter) {
          fieldFormatter = resolveFormatterByFieldType(
            getFieldTypeByName(layerOrLayers, field.fieldName)
          );
        }
      } else {
        fieldFormatter = resolveFormatterByField(layerOrLayers, field);
      }
    }

    return fieldFormatter ?? identity;
  }
}

function createRequestToObservable<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  signal?: AbortSignal
): Observable<T> {
  return new Observable<T>((subscriber) => {
    const abortController = new AbortController();

    subscriber.add(() => {
      abortController.abort();
    });

    if (signal) {
      const onAbort = () => {
        subscriber.complete();
      };

      signal.addEventListener('abort', onAbort);

      subscriber.add(() => {
        signal.removeEventListener('abort', onAbort);
      });
    }

    fn(abortController.signal)
      .then((res) => {
        subscriber.next(res);
        subscriber.complete();
      })
      .catch((err) => {
        subscriber.error(err);
      });
  }).pipe(observeOn(defaultScheduler));
}

function _removeLayersFromRecursively(
  level: number,
  firstMode: boolean,
  layerOrMap: __esri.Map | LayerWithChildren,
  predicate: (
    layer: __esri.Layer | SublayerTypes,
    index: number,
    parent: __esri.Map | LayerWithChildren,
    level: number
  ) => boolean | void
) {
  const collection = EsriUtils.getSublayersCollection(layerOrMap);

  if (collection) {
    const list = collection.toArray();
    let layerAux: __esri.Layer | SublayerTypes;
    for (let i = 0; i < list.length; ++i) {
      layerAux = list[i];
      if (predicate(layerAux, i, layerOrMap, level)) {
        collection.remove(layerAux);
        if (firstMode) {
          return;
        }
      }

      _removeLayersFromRecursively(
        level + 1,
        firstMode,
        layerAux as any,
        predicate
      );
    }
  }
}

function _findParentRecursiveLayer(
  layer: __esri.Layer | SublayerTypes,
  predicate: (
    layer: __esri.Layer | SublayerTypes,
    level: number
  ) => boolean | void,
  deepLevel = Number.MAX_SAFE_INTEGER,
  level = 0
): __esri.Layer | SublayerTypes {
  if (level >= deepLevel) {
    return null;
  }

  const aux = layer as any;
  const parent = aux.parent ?? aux.layer;
  if (!parent || parent instanceof EsriMap) {
    return null;
  }

  if (predicate(parent, level)) {
    return parent;
  }

  return _findParentRecursiveLayer(parent, predicate, deepLevel, level + 1);
}

function _findRecursiveLayer(
  parent: __esri.Map | LayerWithChildren,
  layers: Array<__esri.Layer | SublayerTypes>,
  predicate: (
    layer: __esri.Layer | SublayerTypes,
    index: number,
    parent: __esri.Map | LayerWithChildren,
    level: number
  ) => boolean | void,
  deepLevel = Number.MAX_SAFE_INTEGER,
  level = 0
): __esri.Layer | SublayerTypes {
  let layer: __esri.Layer | SublayerTypes;
  for (let i = 0; i < layers.length; ++i) {
    layer = layers[i];
    if (predicate(layer, i, parent, level)) {
      return layer;
    }

    if (level >= deepLevel) {
      break;
    }

    const collection = EsriUtils.getSublayersCollection(layer as any);
    if (collection) {
      layer = _findRecursiveLayer(
        layer as any,
        collection.toArray(),
        predicate,
        deepLevel,
        level
      );
      if (layer) {
        return layer;
      }
    }
  }

  return null;
}

function _getDeeperLayers(
  layers: Array<__esri.Layer | SublayerTypes>,
  deepLevel = Number.MAX_SAFE_INTEGER,
  level = 0
): Array<__esri.Layer | SublayerTypes> {
  return layers?.flatMap((layer) => {
    let subLayers = EsriUtils.getSublayersCollection(layer as any)?.toArray();

    if (subLayers?.length && level < deepLevel) {
      const layersFlatted = _getDeeperLayers(subLayers, deepLevel, level + 1);
      return layersFlatted;
    } else {
      return [layer];
    }
  });
}

function _flatLayers(
  layers: Array<__esri.Layer | SublayerTypes>,
  deepLevel = Number.MAX_SAFE_INTEGER,
  level = 0
): Array<__esri.Layer | SublayerTypes> {
  return layers?.flatMap((layer) => {
    let subLayers = EsriUtils.getSublayersCollection(layer as any)?.toArray();

    if (subLayers && level < deepLevel) {
      const layersFlatted = _flatLayers(subLayers, deepLevel, level + 1);
      return [layer].concat(layersFlatted);
    } else {
      return [layer];
    }
  });
}

function _observeDeepCollectionLayers(
  collection: __esri.Collection<__esri.Layer | SublayerTypes>,
  deepLevel = Number.MAX_SAFE_INTEGER,
  level = 0
) {
  return new Observable<
    __esri.CollectionChangeEvent<__esri.Layer | SublayerTypes>
  >((subscriber) => {
    let layerMap = new Map<__esri.Layer | SublayerTypes, Subscription>();

    subscriber.add(() => {
      const list = Array.from(layerMap.values());
      for (const x of list) {
        x?.unsubscribe();
      }
    });

    const observeLayersIfCan = (
      layers: Array<__esri.Layer | SublayerTypes>
    ) => {
      if (level > deepLevel) {
        return;
      }

      layers?.forEach((layer) => {
        let collectionAux = EsriUtils.getSublayersCollection(layer as any);

        if (collectionAux) {
          layerMap.set(
            layer,
            _observeDeepCollectionLayers(collectionAux, deepLevel, level + 1)
              .pipe(
                finalize(() => {
                  layerMap.delete(layer);
                })
              )
              .subscribe((e) => {
                subscriber.next(e);
              })
          );
        }
      });
    };

    subscriber.add(
      EsriUtils.on(collection, 'change').subscribe(
        (e: __esri.CollectionChangeEvent<__esri.Layer | SublayerTypes>) => {
          e?.removed?.forEach((layer) => {
            layerMap.get(layer)?.unsubscribe();
          });

          observeLayersIfCan(e?.added);

          subscriber.next(e);
        }
      )
    );

    observeLayersIfCan(collection.toArray());
  });
}

function _isRecursiveLayerVisibleObservable(
  layer: __esri.Layer | SublayerTypes
) {
  let layerVisibleObservable = _observeKeyParamLayer<
    boolean,
    __esri.Layer | SublayerTypes
  >(layer, 'visible');

  const parent = _findParentRecursiveLayer(layer, () => true);
  if (parent) {
    const parentVisibleObservable = _isRecursiveLayerVisibleObservable(parent);

    const layerVisibleObservableAux = layerVisibleObservable;
    layerVisibleObservable = parentVisibleObservable.pipe(
      switchMap((visible) => {
        if (!visible) {
          return of(false);
        }

        return layerVisibleObservableAux;
      })
    );
  }

  return layerVisibleObservable;
}

function _observeKeyParamLayer<T, L = any>(
  layer: L,
  key: keyof L
): Observable<T> {
  return EsriUtils.watch(layer as any, key as any).pipe(
    map((x) => x.newValue),
    startWith(layer[key] as any)
  );
}

function getFieldTypeByName(
  layerOrlayers:
    | QueryableLayer
    | SublayerTypes
    | Array<QueryableLayer | SublayerTypes>,
  fieldName: string
) {
  if (!Array.isArray(layerOrlayers)) {
    layerOrlayers = [layerOrlayers];
  }

  for (const layer of layerOrlayers) {
    const type = ((layer as any)?.fields as __esri.Field[])?.find(
      (x) => x.name === fieldName
    )?.type;
    if (type) {
      return type;
    }
  }

  return null;
}

function resolveFormatterByField(
  layerOrLayers:
    | QueryableLayer
    | SublayerTypes
    | Array<QueryableLayer | SublayerTypes>,
  field: __esri.Field | __esri.Domain | string
): Formatter {
  const fieldName = typeof field === 'string' ? field : field.name;

  let codedValues: __esri.CodedValue[] = (
    ((field as Field)?.domain ?? field) as __esri.CodedValueDomain
  )?.codedValues;

  if (codedValues) {
    const cache = new Map<any, string>();

    return (value) => {
      return EsriUtils.getFieldCodedDomainValue(value, codedValues, cache);
    };
  }

  if (!Array.isArray(layerOrLayers)) {
    layerOrLayers = [layerOrLayers];
  }

  let popupTemplate: __esri.PopupTemplate;
  for (const layer of layerOrLayers) {
    popupTemplate = (layer as any)?.popupTemplate;

    if (popupTemplate) {
      let formatter = resolveFormatterByFieldInfoFormat(
        popupTemplate.fieldInfos?.find((x) => x?.fieldName === fieldName)
          ?.format
      );
      if (formatter) {
        return formatter;
      }
    }
  }

  return resolveFormatterByFieldType(
    (field as Field)?.type ?? getFieldTypeByName(layerOrLayers, fieldName)
  );
}

function resolveFormatterByFieldType(type: __esri.Field['type']): Formatter {
  switch (type) {
    case 'date':
      return FormatterUtils.formatDateTime;
    case 'double':
      return (value) => {
        return FormatterUtils.formatNumber(value, 2);
      };
  }

  return null;
}

function resolveFormatterByFieldInfoFormat(
  fieldInfoFormat: FieldInfoFormat
): Formatter {
  if (!fieldInfoFormat) {
    return null;
  }

  if (
    fieldInfoFormat.dateFormat &&
    (fieldInfoFormat.dateFormat as any) !== 'default'
  ) {
    const options = intl.convertDateFormatToIntlOptions(
      fieldInfoFormat.dateFormat as any
    );
    return (value) => {
      if (ObjectUtils.isEmpty(value)) {
        return null;
      }

      return intl.formatDate(value, options);
    };
  } else if (typeof fieldInfoFormat.places === 'number') {
    const options = {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: fieldInfoFormat.places,
      useGrouping: fieldInfoFormat.digitSeparator,
    };

    return (value) => {
      if (ObjectUtils.isEmpty(value)) {
        return null;
      }

      return intl.formatNumber(value, options as any);
    };
  }

  return null;
}

function identity(value) {
  return value;
}
